import React from 'react'
import { AITripPreferenceStep } from './GenerateTripPage'

export interface LocationFact {
  text: string;
  detail?: string;
}

type LocationFactsMap = {
  [key: string]: Array<LocationFact>;
};

export const locationFacts: LocationFactsMap = {
  Sydney: [
    {
      text: 'The Sydney Opera House uses 1,056,006 roof tiles',
      detail: 'Those iconic white tiles are made from a Swedish porcelain — and they actually clean themselves in the rain!',
    },
    {
      text: 'Sydney Harbour is the world\'s largest natural harbour',
      detail: 'It stretches over 240 km of shoreline and holds more water than the world\'s next four largest natural harbours combined.',
    },
    {
      text: 'Enough steel went into the Sydney Harbour Bridge to build eight Eiffel Towers',
      detail: 'Its arch spans 503 m and the top is 134 m above sea level at mid-span.',
    },
    {
      text: 'The world\'s first surf lifesaving club was founded at Bondi Beach in 1907',
      detail: 'Those red-and-yellow flags you see started right here.',
    },
    {
      text: 'Sydney\'s Royal Botanic Garden is Australia\'s oldest scientific institution',
      detail: 'It\'s been cultivating exotic and native plants since 1816.',
    },
    {
      text: 'There\'s a secret beach at Milk Beach in Vaucluse',
      detail: 'Tucked beneath the cliffs of Watsons Bay, it\'s reached via a short but steep walking path.',
    },
    {
      text: 'Blue Whales pass near the coast at Cape Solander',
      detail: 'From May to November, this is one of the best whale-watching spots in the Southern Hemisphere.',
    },
    {
      text: 'The Rocks district is Sydney\'s oldest neighbourhood',
      detail: 'Walk its cobbled laneways to feel like you\'ve stepped back into the 1800s—complete with hidden pubs.',
    },
  ],
  'Nusa Dua': [
    {
      text: "'Nusa Dua' means 'two islands'",
      detail: "It refers to the twin islets off the southern tip of Bali's Bukit Peninsula.",
    },
    {
      text: 'Nusa Dua was built from scratch in the 1970s',
      detail: "The Bali Tourism Development Corporation reclaimed land to create Bali's first purpose-built resort enclave.",
    },
    {
      text: "Nusa Dua hosts one of Indonesia's largest convention centers",
      detail: 'The Bali International Convention Centre can seat over 6,000 delegates.',
    },
    {
      text: "The Pasifika Museum holds the world's largest collection of Pacific and Asia-Pacific art",
      detail: 'Over 2,500 works span from New Zealand Maori carvings to Indonesian batiks.',
    },
    {
      text: "The 'Water Blow' at Tanjung Benoa sends waves 10 m into the air",
      detail: 'A natural rock formation on the eastern edge of Nusa Dua—best seen at high tide.',
    },
    {
      text: 'The Bali Collection is Nusa Dua’s open-air shopping and dining complex',
      detail: 'It spans more than 30,000 m² with dozens of boutiques, galleries, and eateries.',
    },
    {
      text: 'Turtle Island (Pulau Serangan) is just 5 km away',
      detail: 'It’s a conservation hub where you can release baby sea turtles into the ocean.',
    },
    {
      text: 'Geger Beach is home to an ancient Balinese temple',
      detail: 'Pura Geger hosts weekly offerings overlooking the bay’s calm, turquoise waters.',
    },
  ],
  London: [
    {
      text: 'London’s buses weren’t always red',
      detail: 'They were once a mix of colors depending on the route, until red was standardized in 1907.',
    },
    {
      text: 'The London Underground is the world’s oldest metro system',
      detail: 'It opened in 1863 and now covers over 400 km of track.',
    },
    {
      text: 'Big Ben doesn’t refer to the tower',
      detail: 'It’s the nickname of the Great Bell inside the Elizabeth Tower.',
    },
    {
      text: 'There are over 170 museums in London',
      detail: 'Many, like the British Museum and Tate Modern, are free to enter.',
    },
    {
      text: 'The River Thames has over 30 bridges in Greater London',
      detail: 'The oldest is London Bridge, though the current structure dates from 1973.',
    },
    {
      text: 'Tower Bridge once raised its bascules over 6,000 times a year',
      detail: 'Today, it lifts only around 800 times annually—still an impressive feat of engineering.',
    },
    {
      text: 'London has more than 8 million trees',
      detail: 'That’s nearly one tree for every resident, making it one of the world’s greenest major cities.',
    },
    {
      text: 'The Shard is the tallest building in the UK',
      detail: 'It rises 310 meters above the city and houses offices, restaurants, and a hotel.',
    },
  ],
  Seminyak: [
    {
      text: "Seminyak's beach is famous for its colourful beanbags and umbrellas",
      detail: "It's the ultimate spot for sunset cocktails and beachfront lounging.",
    },
    {
      text: "'Seminyak' comes from the Balinese words for 'reef' and 'harbor'",
      detail: "Over time it's evolved from a quiet fishing village into a luxury enclave.",
    },
    {
      text: 'Seminyak hosts over 500 private villas—one of the highest luxury-villa densities in Bali',
      detail: 'Many come with plunge pools, open-air living rooms, and personal butlers.',
    },
    {
      text: 'Petitenget Temple dates back to the 6th century',
      detail: 'Each year it throws a vibrant Odalan festival of dance, music, and offerings.',
    },
    {
      text: "Potato Head Beach Club's infinity pool is built from 500,000 upcycled glass bottles",
      detail: "It's a design marvel—and a sustainable one, too.",
    },
    {
      text: 'Jalan Kayu Aya is home to the Seminyak Flea Market',
      detail: 'Over 60 stalls showcase local artisans’ crafts, fashion, and homewares.',
    },
    {
      text: 'There’s a “secret” rooftop garden at the Seminyak Village mall',
      detail: 'It offers ocean views you’d never guess were just above a shopping center.',
    },
    {
      text: 'Double Six Beach was once a refueling point for Japanese seaplanes in WWII',
      detail: 'Today it’s a laid-back stretch perfect for an early-morning paddle.',
    },
  ],
  Canggu: [
    {
      text: "Echo Beach's sand is black",
      detail: 'Volcanic minerals from Mount Agung give it that dramatic hue.',
    },
    {
      text: 'Tanah Lot Temple sits on a rocky islet just 15 km from Canggu',
      detail: "It's one of Bali's most iconic sunset-viewing spots.",
    },
    {
      text: 'Berawa Beach offers waves up to 3 meters high',
      detail: "It's an intermediate-to-advanced surfers' paradise.",
    },
    {
      text: 'Canggu was a sleepy rice-farming village until the late 2000s',
      detail: 'Now it marries bohemian charm with boutique cool.',
    },
    {
      text: 'The Love Anchor Market rotates local artisans every weekend',
      detail: 'Handmade jewellery, home décor, and health-food stalls await.',
    },
    {
      text: 'Canggu Circle Mall features Bali’s first curved digital-media façade',
      detail: 'At night it pulses with LED art and event announcements.',
    },
    {
      text: 'Deus Ex Machina began here as a motorcycle workshop',
      detail: 'Today it’s a cult lifestyle brand for surf, art, and coffee lovers.',
    },
    {
      text: 'Pererenan Beach’s boardwalk is known as Bali’s best “Sunset Point”',
      detail: 'Walk above the waves for panoramic ocean views.',
    },
  ],
  Ubud: [
    {
      text: "'Ubud' comes from the Balinese word for medicine",
      detail: "The area was once renowned for its healing herbs and traditional Balinese healers ('bali ubad').",
    },
    {
      text: 'The Sacred Monkey Forest Sanctuary is home to around 700 long-tailed macaques',
      detail: 'It also protects three ancient Hindu temples dating back to the 14th century.',
    },
    {
      text: 'Puri Saren Royal Palace was built in the early 1800s',
      detail: 'Tonight it still hosts nightly Balinese dance performances in its central courtyard.',
    },
    {
      text: 'Taman Saraswati (the Water Palace) features a floating lotus pond stage',
      detail: 'The 1951 temple is dedicated to the goddess of learning, Saraswati, and often showcases kecak dance.',
    },
    {
      text: "The terraced rice fields around Ubud are part of Bali's UNESCO-listed Subak irrigation system",
      detail: 'This communal water-sharing practice dates back to the 9th century.',
    },
    {
      text: 'Goa Gajah (“Elephant Cave”) is an archaeological site from the 11th century',
      detail: 'Its carved rock entrance and bathing pools were only rediscovered in 1923.',
    },
    {
      text: 'The Campuhan Ridge Walk was originally a royal horse trail',
      detail: 'Today it’s a free, easy 2-km hillside trek offering panoramic valley views at sunrise.',
    },
    {
      text: 'The Bali Spirit Festival blends yoga, dance and live music',
      detail: 'It’s one of Asia’s premier wellness and cultural gatherings, held each spring in Ubud.',
    },
  ],
  Kuta: [
    {
      text: 'Kuta was originally a quiet fishing village',
      detail: "It didn't become a global backpacker hotspot until the 1970s surfer boom.",
    },
    {
      text: 'Kuta Beach stretches about 5 km from Tuban up to Legian',
      detail: "One of Bali's longest continuous shorelines, it's perfect for sunrise strolls.",
    },
    {
      text: "Waterbom Bali in Kuta has been voted Asia's #1 waterpark multiple times",
      detail: 'Spanning 3.8 ha, it boasts over 17 adrenaline-pumping slides.',
    },
    {
      text: "'Kuta' comes from the old Balinese word kutha, meaning fortress",
      detail: 'Historians believe a small stronghold once guarded this stretch of coast.',
    },
    {
      text: 'Every August Kuta hosts the Kuta Karnival',
      detail: 'Expect sand-sculpting contests, beach soccer tournaments and vibrant parades.',
    },
    {
      text: 'Kuta Art Market lights up nightly with 100+ stalls',
      detail: 'It’s the best place to haggle for hand-woven ikats, carved masks and silver trinkets.',
    },
    {
      text: 'The area still celebrates full-moon ceremonies at local temples',
      detail: 'Expect gamelan music, fragrant offerings, and elaborate dances.',
    },
    {
      text: 'Ngurah Rai International Airport is just minutes from Kuta',
      detail: 'That makes it one of Bali’s most conveniently located beach destinations.',
    },
  ],
  Legian: [
    {
      text: 'Legian sits just north of Kuta but offers a slightly quieter vibe',
      detail: 'It shares those legendary Bali sunsets—just with fewer crowds.',
    },
    {
      text: "'Legian' in old Balinese literally means 'good sand'",
      detail: 'A nod to its soft, golden shoreline that stretches for over 2 km.',
    },
    {
      text: "La Plancha in Legian pioneered Bali's beachfront lounge scene back in 2006",
      detail: 'Its rainbow of umbrellas and beanbags set the template for beach bars everywhere.',
    },
    {
      text: 'Jalan Padma Utara is lined with boutiques selling local silversmith craft',
      detail: "From delicate filigree earrings to bold statement cuffs, you'll find it here.",
    },
    {
      text: 'Legian celebrates purification ceremonies at Pura Dalem',
      detail: 'On full-moon nights, locals gather for gamelan music and temple dances.',
    },
    {
      text: 'Padma Beach in Legian is a popular surf spot for beginners',
      detail: 'Gentle waves and surf schools make it ideal for learning.',
    },
    {
      text: 'Legian rooftops offer panoramic views of South Bali',
      detail: 'Perfect for sunset drinks with ocean breezes.',
    },
    {
      text: 'The Double Six Club helped launch Bali’s nightlife scene',
      detail: 'Though now closed, its beachfront location sparked the lounge-bar trend.',
    },
  ],
  Jimbaran: [
    {
      text: 'Jimbaran Bay was once a humble fishing village famous for its early-morning fish markets',
      detail: 'The Kedonganan Fish Market still bustles at dawn as local fishermen unload their daily catch.',
    },
    {
      text: 'Jimbaran Beach stretches for about 4 km of golden sand',
      detail: 'Its calm, gentle waters make it perfect for families and budding stand-up paddleboarders.',
    },
    {
      text: "Jimbaran's beachfront seafood cafés let you pick your own catch",
      detail: 'Choose from fish, prawns, clams or lobster, then watch chefs grill it right on the sand.',
    },
    {
      text: "Jimbaran's famous sunset dinners under tiki torches began in the 1970s",
      detail: "What started as fishermen hosting beach barbecues has become Bali's iconic dining experience.",
    },
    {
      text: 'Uluwatu Temple sits on a cliff just west of Jimbaran Bay',
      detail: 'This 11th-century sea temple offers dramatic ocean views and nightly kecak dance performances.',
    },
    {
      text: 'Traditional jukung boats still float offshore',
      detail: 'Painted in bright hues, these outriggers are the same vessels local fishermen have used for generations.',
    },
    {
      text: 'Jimbaran is a popular spot for plane-spotting',
      detail: 'Its proximity to the airport gives beachgoers views of jets arriving overhead.',
    },
    {
      text: 'Limestone bluffs form a natural amphitheatre on the bay’s edge',
      detail: 'They frame some of Bali’s most dramatic sunsets.',
    },
  ],
  Uluwatu: [
    {
      text: "'Pura Luhur Uluwatu' perches atop a 70 m limestone cliff",
      detail: "Its name means 'Divine Temple of the Land's End,' and it's one of Bali's six key sea temples.",
    },
    {
      text: 'Uluwatu Temple is guarded by a troupe of mischievous macaques',
      detail: "These long-tailed monkeys are notorious for 'borrowing' sunglasses and cameras from unsuspecting visitors.",
    },
    {
      text: 'You can catch the famous Kecak Fire Dance here at sunset',
      detail: 'Over a hundred chanting men circle a blazing fire, reenacting legends against the vivid ocean horizon.',
    },
    {
      text: 'Uluwatu is a world-class surf mecca, with waves up to 6 m high',
      detail: "The 'Uluwatu Right' break has drawn pro surfers since the 1970s.",
    },
    {
      text: "There's a secret cave beneath the cliff",
      detail: 'Steps carved into the rock lead down to a sheltered grotto once used for meditation.',
    },
    {
      text: 'Cliffside shrines line the Uluwatu walking path',
      detail: 'Pilgrims still leave flower offerings and incense at each small pavilion.',
    },
    {
      text: 'Padang Padang Beach lies just below the temple',
      detail: 'Accessible through a narrow rock tunnel, it’s famous for its powder-white sand and sheltered cove.',
    },
    {
      text: 'Blowholes beneath the cliffs send water jets into the sky',
      detail: 'They’re best viewed at high tide from the cliffside trail.',
    },
  ],
  'Gold Coast': [
    {
      text: 'The Gold Coast has over 70 km of coastline',
      detail: 'It’s home to some of Australia’s most iconic surf beaches, including Surfers Paradise and Burleigh Heads.',
    },
    {
      text: 'Surfers Paradise was originally called Elston',
      detail: 'The name was changed in 1933 to attract tourism, and it worked.',
    },
    {
      text: 'The Gold Coast has more canals than Venice',
      detail: 'Its 400 km of navigable waterways weave through residential neighbourhoods and resort zones.',
    },
    {
      text: 'The Q1 building is one of the tallest residential towers in the world',
      detail: 'Standing at 322 meters, it offers panoramic views from its SkyPoint observation deck.',
    },
    {
      text: 'The Gold Coast hosts over 12 million visitors annually',
      detail: 'Tourism is a major driver of the local economy alongside film production and education.',
    },
    {
      text: 'Burleigh Head National Park offers rainforest trails just minutes from the beach',
      detail: 'You can spot whales from the headland during migration season.',
    },
    {
      text: 'The area has a booming arts and culture scene',
      detail: 'HOTA (Home of the Arts) is a world-class cultural precinct with galleries, theatres, and outdoor concerts.',
    },
    {
      text: 'The Gold Coast was a key filming location for major Hollywood movies',
      detail: 'Its Village Roadshow Studios hosted productions like Thor: Ragnarok and Aquaman.',
    },
  ],
  Generic: [
    {
      text: 'Airplane food tastes different on purpose',
      detail: 'Cabin air dulls taste buds and dries noses, so airlines often double the salt to compensate.',
    },
    {
      text: 'There are nearly 30 places named "London" around the world',
      detail: 'Beyond the UK capital, you’ll find Londons in Canada, Ohio, Kiribati and more.',
    },
    {
      text: 'The world’s most visited city isn’t New York or Paris',
      detail: 'Bangkok regularly tops global tourism charts, thanks to its food, nightlife and affordability.',
    },
    {
      text: 'The London Eye was never meant to be permanent',
      detail: 'Built for the millennial celebrations in 2000, the wheel proved so popular it stayed.',
    },
    {
      text: 'The world’s shortest scheduled flight takes under 60 seconds',
      detail: 'Loganair’s Westray–Papa Westray hop in Scotland spans just 2.7 km.',
    },
    {
      text: 'Travel supports more than one in ten jobs worldwide',
      detail: 'Pre-pandemic, tourism employed roughly 330 million people across the globe.',
    },
    {
      text: 'Rome hosts a museum devoted entirely to pasta',
      detail: 'The Museo della Pasta has 11 rooms exploring its history, science, shapes and art.',
    },
    {
      text: 'The Eiffel Tower was nearly torn down—until radio transmissions saved it',
      detail: 'Slated for removal in 1909, its new role as a strategic radio mast secured its future.',
    },
    {
      text: 'Some airports ignore daylight saving time',
      detail: 'Phoenix Sky Harbor, for instance, stays on Mountain Standard Time all year.',
    },
    {
      text: 'You can fly around the world faster going east',
      detail: 'Westerly jet streams give eastbound aircraft a helpful tail-wind that can slice hours off long-haul trips.',
    },
    {
      text: 'Finland has almost as many saunas as cars',
      detail: 'Around 3 million saunas serve 5.5 million Finns—close to the nation’s 3.7 million passenger vehicles.',
    },
    {
      text: 'Tromsø, Norway sees no sunrise for six weeks',
      detail: 'During polar night (late November to mid-January) the sun never rises above the horizon.',
    },
    {
      text: 'Switzerland technically has no capital city',
      detail: 'Bern hosts parliament, but the Swiss constitution never designates an official capital.',
    },
    {
      text: 'The Great Wall of China isn’t a single wall',
      detail: 'It’s a network of fortifications stretching about 21,000 km across northern China.',
    },
    {
      text: 'Malana, India, speaks a mysterious local dialect',
      detail: 'Kanashi is unique to the village; legends link it to Greek soldiers, but linguists trace Tibeto-Burman roots.',
    },
    {
      text: 'Monaco is smaller than New York’s Central Park',
      detail: 'The principality covers 2.02 km², while Central Park spans roughly 3.4 km².',
    },
    {
      text: 'You can ride a zipline from Spain to Portugal',
      detail: 'Limite Zero glides over the Guadiana River—the world’s only cross-border zipline.',
    },
    {
      text: 'Canada has more lakes than the rest of the world combined',
      detail: 'Roughly 60% of the planet’s lakes ≥ 0.1 km² lie within Canadian borders.',
    },
    {
      text: 'La Gomera in the Canaries has a whistled language',
      detail: 'Silbo Gomero lets residents converse across deep valleys using only whistles.',
    },
    {
      text: 'Bhutan measures progress in Gross National Happiness',
      detail: 'The index guides government decisions alongside traditional economic metrics.',
    },
    {
      text: 'London cabbies memorise around 25,000 streets',
      detail: 'Earning ‘The Knowledge’ licence can take years of scooter-based studying.',
    },
    {
      text: 'You can mail a coconut in Hawaii with no packaging',
      detail: 'Write an address, add stamps and the USPS will deliver the bare nut—no box required.',
    },
  ],
}

export const fallbackImageIds: Array<string> = [
  '5ozks72rwzry7f9k2m9n',
  'rpuuftaccpy35ymgxn86',
  'pm9wsigf28xbtjdrrnv',
  'khdm8sqv4llroip0gr3fa',
  'o67q4bymkgw9zeo7gzg',
]

export const stepImageIds: Record<AITripPreferenceStep, string | undefined> = {
  start: undefined,
  pace: '9w6i5jqu19hhyebmmzo',
  proximity: 'cnzq14sdc2j0afsjvvr',
  budget: 'kkflnjvd7sradtskysz',
  theme: 'y5mptild2qduora9qhs',
  mustDo: '9ktjc7j8r7tqztli96v',
  foodStyle: 'nrhcjilarfw651sgkkmn',
  freeText: undefined,
}

export const animatedBackgroundIds = [
  'trip-fade-1',
  'trip-fade-2',
  'trip-fade-3',
]

export function AITripTitles(hotelCity: string) {
  return [
    <>We're shaping your <i>{hotelCity}</i> itinerary...</>,
    'Understanding your trip preferences and travel style...',
    'Reading reviews, blogs and local guides...',
    'Researching the best things to do...',
    'Finding restaurants and attractions near your hotel...',
    'Curating the right experiences for each day...',
    'Checking timings and transfers so your day flows smoothly...',
    'Finding hidden gems beyond the tourist spots...',
    'Personalising your trip to match your style...',
    'Making sure every moment counts...',
    'Scouring the internet for the best insider tips from locals...',
    'Double-checking details so nothing gets missed...',
    'Adding the final touches...',
  ]
}
